export const MAIN_SIDE_NAV_SHRINK_DURATION_MS = 300;

const findTriggerIcon = (contentId: string) => {
  const trigger = document.querySelector(`[aria-controls="${CSS.escape(contentId)}"]`);

  return trigger?.querySelector('svg') ?? trigger;
};

/** `transform-origin` for `element` that points at the icon of the rail item controlling `contentId`. */
export const getMainSideNavTriggerTransformOrigin = (element: Element, contentId: string) => {
  const icon = findTriggerIcon(contentId);

  if (!icon) {
    return undefined;
  }

  const iconRect = icon.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const x = iconRect.left + iconRect.width / 2 - elementRect.left;
  const y = iconRect.top + iconRect.height / 2 - elementRect.top;

  return `${x}px ${y}px`;
};

type ShrinkIntoMainSideNavTriggerParams = {
  /** Scrim behind `element`; fades out alongside the shrink. */
  backdrop?: HTMLElement | null;
  contentId: string;
  element: HTMLElement;
};

/**
 * Same close motion as `MainSideNavOverlayTray`'s `shrinkIntoTrigger`, for elements that unmount
 * without a Radix exit animation. Resolves right away when there is no rail icon or motion is reduced.
 */
export const shrinkIntoMainSideNavTrigger = async ({
  backdrop,
  contentId,
  element,
}: ShrinkIntoMainSideNavTriggerParams) => {
  const transformOrigin = getMainSideNavTriggerTransformOrigin(element, contentId);

  if (!transformOrigin || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // The caller closes once the motion ends, so a click meanwhile could submit twice. Only `element`:
  // an inert backdrop drops out of hit-testing and lets clicks through to the page.
  element.inert = true;
  element.style.transformOrigin = transformOrigin;
  backdrop?.animate([{}, { backgroundColor: 'transparent' }], {
    duration: MAIN_SIDE_NAV_SHRINK_DURATION_MS,
    fill: 'forwards',
  });

  const animation = element.animate(
    [
      { opacity: 1, transform: 'scale(1)' },
      { offset: 0.75, opacity: 1 },
      { opacity: 0, transform: 'scale(0)' },
    ],
    { duration: MAIN_SIDE_NAV_SHRINK_DURATION_MS, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
  );

  // Rejects with AbortError when the element is dismissed mid-shrink; callers check whether it is still mounted.
  await animation.finished.catch(() => {});
};
